import React, { createContext, useContext, useState, useCallback } from "react";
import { API_BASE as API } from "../api/api";
import {
  POLICY_VERSION,
  DEFAULT_CONSENT,
  getVisitorId,
  getStoredConsent,
  setStoredConsent,
} from "../utils/consent";

const ConsentContext = createContext(null);

export function ConsentProvider({ children }) {
  const [consent, setConsent] = useState(() => getStoredConsent() || { ...DEFAULT_CONSENT });
  const [hasDecided, setHasDecided] = useState(() => !!getStoredConsent());

  const persist = useCallback((categories, source) => {
    const next = { ...DEFAULT_CONSENT, ...categories, necessary: true };
    setStoredConsent(next);
    setConsent(next);
    setHasDecided(true);

    fetch(`${API}/consent`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        visitor_id: getVisitorId(),
        functional: next.functional,
        analytics: next.analytics,
        personalisation: next.personalisation,
        marketing: next.marketing,
        policy_version: POLICY_VERSION,
        source,
      }),
    }).catch(() => {});
  }, []);

  const acceptAll = useCallback((source = "banner") => {
    persist({ functional: true, analytics: true, personalisation: true, marketing: true }, source);
  }, [persist]);

  const rejectNonEssential = useCallback((source = "banner") => {
    persist({ functional: false, analytics: false, personalisation: false, marketing: false }, source);
  }, [persist]);

  const customise = useCallback((categories, source = "preference_centre") => {
    persist(categories, source);
  }, [persist]);

  // Mirrors a consent decision the SERVER already resolved for this device
  // (e.g. adopted from another device on account link) into local state —
  // never re-POSTs it, since the server already has the real record. This is
  // what takes the banner down on a new device the instant sign-in resolves,
  // instead of making someone re-decide something they already told us on
  // another laptop. A stale-policy record (server already filters this, but
  // defend here too) is ignored, same as a locally-stored one.
  const adoptFromServer = useCallback((record) => {
    if (!record || record.policy_version !== POLICY_VERSION) return;
    const next = {
      ...DEFAULT_CONSENT,
      functional: record.functional,
      analytics: record.analytics,
      personalisation: record.personalisation,
      marketing: record.marketing,
    };
    setStoredConsent(next);
    setConsent(next);
    setHasDecided(true);
  }, []);

  const value = {
    ...consent,
    hasDecided,
    policyVersion: POLICY_VERSION,
    acceptAll,
    rejectNonEssential,
    customise,
    adoptFromServer,
  };

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
}

export function useConsent() {
  const ctx = useContext(ConsentContext);
  if (!ctx) throw new Error("useConsent must be used within a ConsentProvider");
  return ctx;
}
